
export class DateTime {
    private readonly _date: Date;
    private static _parse(input?: string | number | Date | null): Date {
        if (input instanceof Date) return new Date(input.getTime());
        if (typeof input === 'number') return new Date(input);
        if (input === undefined || input === null) return new Date(NaN);
        if (typeof input !== 'string') return new Date(NaN);

        if (/^\d{4}-\d{2}-\d{2}$/.test(input)) {
            const date = new Date(`${input}T00:00:00`);
            const [y, m, d] = input.split('-').map(Number);
            const isValid = date.getFullYear() === y && (date.getMonth() + 1) === m && date.getDate() === d;
            return isValid ? date : new Date(NaN);
        }

        return new Date(input);
    }

    constructor(input?: string | number | Date | null) {
        this._date = arguments.length === 0 ? new Date() : DateTime._parse(input);
    }

    static isValid(input?: string | number | Date | null): boolean {
        const d = DateTime._parse(input);
        return !isNaN(d.getTime());
    }

    isValid(): boolean {
        return !isNaN(this._date.getTime());
    }

    format(template: string): string {
        if (!this.isValid()) return '';

        const year = this._date.getFullYear();
        const month = this._date.getMonth() + 1;
        const day = this._date.getDate();
        const hours = this._date.getHours();
        const minutes = this._date.getMinutes();
        const seconds = this._date.getSeconds();

        const map: Record<string, string | number> = {
            YYYY: year,
            MM: String(month).padStart(2, '0'),
            DD: String(day).padStart(2, '0'),
            HH: String(hours).padStart(2, '0'),
            mm: String(minutes).padStart(2, '0'),
            ss: String(seconds).padStart(2, '0'),
        };

        return template.replace(/YYYY|MM|DD|HH|mm|ss/g, (matched) => String(map[matched]));
    }

    toDatePart(): string | null {
        if (!this.isValid()) return null;
        return this.format('YYYY-MM-DD');
    }

    toTimePart(): string | null {
        if (!this.isValid()) return null;
        return this.format('HH:mm');
    }

    toISOString(): string | null {
        if (!this.isValid()) return null;
        return this._date.toISOString();
    }

    isBefore(other: DateTime | Date | string | number | null): boolean {
        const otherDt = other instanceof DateTime ? other : new DateTime(other);
        if (!this.isValid() || !otherDt.isValid()) return false;
        return this._date.getTime() < otherDt._date.getTime();
    }

    isAfter(other: DateTime | Date | string | number | null): boolean {
        const otherDt = other instanceof DateTime ? other : new DateTime(other);
        if (!this.isValid() || !otherDt.isValid()) return false;
        return this._date.getTime() > otherDt._date.getTime();
    }

    static parse(input: string | number | Date | null): DateTime {
        return new DateTime(input);
    }

    static now(): DateTime {
        return new DateTime(new Date());
    }
}

export function dt(input?: string | number | Date | null): DateTime {
    if (arguments.length === 0) return new DateTime();
    return new DateTime(input);
}
